/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
// Copied from confluence/next/packages/navigation-search-dialog/src/suppliers/utils/ConfluenceResultMeta.tsx
import React from 'react';

import { styled } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import { SearchConfluenceSubtype } from '@atlassian/search-client/common/types';

import { messages } from './messages';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ConfluenceResultMetaWrapper = styled.span({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'center',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BreadcrumbsWrapper = styled.div({
	paddingTop: token('space.025'),
	'nav > ol': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'li:not(:last-child)::after': {
			paddingTop: '0px',
			paddingRight: token('space.050'),
			paddingBottom: '0px',
			paddingLeft: token('space.050'),
			width: '4px',
		},
		flexWrap: 'nowrap',
	},
	'li a': {
		height: '100%',
		pointerEvents: 'none',
		alignItems: 'center',
		font: token('font.body.small'),
		padding: '0px',
	},
	'li button': {
		pointerEvents: 'none',
		font: token('font.body.small'),
		alignItems: 'center',
		height: '100%',
		padding: '0px',
	},
	'li button span': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '16px',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ConfluenceTypeWrapper = styled.span({
	color: token('color.text.subtle'),
	textTransform: 'capitalize',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ConfluenceMetaSpacerWrapper = styled.span({
	color: token('color.text.subtle'),
	marginTop: '0px',
	marginRight: token('space.050'),
	marginBottom: '0px',
	marginLeft: token('space.050'),
});

type ConfluenceResultAncestor = {
	id: string;
	title: string | null;
};

type ConfluenceResultSpace = {
	id: string | null;
	name?: string | null;
};

type ConfluenceResultMetaProps = {
	resultContentType: string;
	resultContentSubtype?: SearchConfluenceSubtype;
	resultAncestors?: ConfluenceResultAncestor[] | null;
	resultSpace?: ConfluenceResultSpace | null;
};

export const ConfluenceResultMeta: ({
	resultContentType,
	resultContentSubtype,
	resultAncestors,
	resultSpace,
}: ConfluenceResultMetaProps) => React.JSX.Element = ({
	resultContentType,
	resultContentSubtype,
	resultAncestors,
	resultSpace,
}: ConfluenceResultMetaProps): React.JSX.Element => {
	const breadcrumbs = getBreadcrumbs(resultAncestors, resultSpace);
	const isLiveDoc =
		resultContentSubtype === SearchConfluenceSubtype.LIVE &&
		fg('confluence_live_pages_open_beta_trait_opted_in');

	return (
		<ConfluenceResultMetaWrapper>
			<ConfluenceTypeWrapper>Confluence</ConfluenceTypeWrapper>
			<ConfluenceMetaSpacerWrapper>•</ConfluenceMetaSpacerWrapper>
			<ConfluenceTypeWrapper>
				{isLiveDoc ? <FormattedMessage {...messages.liveDoc} /> : resultContentType}
			</ConfluenceTypeWrapper>
			{breadcrumbs?.length && (
				<>
					<ConfluenceMetaSpacerWrapper>•</ConfluenceMetaSpacerWrapper>
					<BreadcrumbsWrapper>
						<Breadcrumbs maxItems={2} isExpanded={false} onExpand={(e: any) => e.preventDefault()}>
							{breadcrumbs.map(({ id, title }, itemIndex) => (
								<BreadcrumbsItem
									key={id}
									text={title || ''}
									truncationWidth={
										itemIndex === breadcrumbs.length - 1 || itemIndex === 0 ? 130 : 50
									}
								/>
							))}
						</Breadcrumbs>
					</BreadcrumbsWrapper>
				</>
			)}
		</ConfluenceResultMetaWrapper>
	);
};

export const getBreadcrumbs: (
	ancestors: ConfluenceResultAncestor[] | null | undefined,
	space: ConfluenceResultSpace | null | undefined,
) => {
	id?: string | null;
	title?: string | null;
}[] = (
	ancestors: ConfluenceResultAncestor[] | null | undefined,
	space: ConfluenceResultSpace | null | undefined,
) => {
	let breadcrumbs: { id?: string | null; title?: string | null }[] = [];

	if (space) {
		const spaceBreadcrumb = { id: space?.id, title: space?.name };

		if (!ancestors || ancestors.length === 0) {
			breadcrumbs = [spaceBreadcrumb];
		} else if (ancestors.length >= 1) {
			breadcrumbs = [spaceBreadcrumb, ...ancestors.slice(1)];
		}
	}

	return breadcrumbs;
};
